import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import ReactDOM from "react-dom";
import {
  HiArrowRight,
  HiArrowLeft,
  HiX,
  HiPhotograph,
  HiOutlineViewGrid,
  HiCheck,
  HiLink,
} from "react-icons/hi";
import { MdCalendarToday, MdLocalOffer } from "react-icons/md";
import { FaUserCircle } from "react-icons/fa";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";

// ─── useInView ─────────────────────────────────────────────────────────────────
const useInView = (ref, threshold = 0.12) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

const formatDate = (isoString) => {
  if (!isoString) return "";
  return new Date(isoString).toLocaleDateString("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

// ─── Hero ──────────────────────────────────────────────────────────────────────
const Hero = ({ story }) => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  if (!story) return null;

  return (
    <section className="bg-white pt-10 pb-0 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div
          className="flex items-center gap-2 mb-8 text-xs text-[#a09880] font-medium tracking-wide"
          style={{
            opacity: loaded ? 1 : 0,
            transition: "opacity 0.6s ease 0.1s",
          }}
        >
          <a href="/" className="hover:text-[#c9862a] transition-colors">
            Beranda
          </a>
          <span>/</span>
          <a
            href="/portfolio"
            className="hover:text-[#c9862a] transition-colors"
          >
            Portfolio
          </a>
          <span>/</span>
          <span className="text-[#c9862a]">Studi Kasus</span>
        </div>

        {/* Category tag */}
        <div
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(16px)",
            transition: "all 0.7s ease 0.2s",
          }}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#c9862a]" />
            <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
              {story.kategori}
            </span>
          </div>
        </div>

        {/* Title */}
        <h1
          className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#1a1a18] leading-[1.05] mb-8"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.9s ease 0.3s",
          }}
        >
          {story.judul}
        </h1>

        {/* Meta row */}
        <div
          className="flex flex-wrap items-center gap-5 mb-10 pb-8 border-b border-[#e8e0d0]"
          style={{
            opacity: loaded ? 1 : 0,
            transition: "opacity 0.8s ease 0.5s",
          }}
        >
          <div className="flex items-center gap-2 text-[#6b6456] text-sm">
            <MdCalendarToday className="text-[#c9862a]" />
            <span>{formatDate(story.createdAt)}</span>
          </div>
          <span className="w-1.5 h-1.5 rounded-full bg-[#c9862a]" />
          <div className="flex items-center gap-2 text-[#6b6456] text-sm">
            <MdLocalOffer className="text-[#c9862a]" />
            <span className="border-b border-[#6b6456]/30 hover:border-[#c9862a] hover:text-[#c9862a] cursor-pointer transition-colors">
              {story.kategori}
            </span>
          </div>
          <span className="w-1.5 h-1.5 rounded-full bg-[#c9862a]" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#c9862a]" />
          <span className="text-[#a09880] text-sm">
            Oleh{" "}
            <strong className="text-[#3a3a34]">Tim Rumoh Pangan Aceh</strong>
          </span>
        </div>

        {/* Hero image */}
        <div
          className="w-full overflow-hidden relative"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.9s ease 0.4s",
          }}
        >
          {story.url ? (
          <img
            src={story.url}
            alt={story.judul}
            className="w-full h-[420px] md:h-[520px] object-cover"
          />
          ) : (
            <div className="w-full h-[420px] md:h-[520px] bg-[#f7f3ec] flex items-center justify-center text-[#a09880]">
              No Image
            </div>
          )}
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#c9862a] to-transparent" />
          <div className="absolute bottom-0 right-0 bg-[#1a1a18]/70 px-4 py-2">
            <span className="text-white/60 text-xs tracking-wide">
              Dokumentasi Rumoh Pangan Aceh
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── AlbumLightbox: fullscreen carousel viewer ─────────────────────────────────
const AlbumLightbox = ({ album, activeIndex, onClose, onPrev, onNext, onSelect }) => {
  const [showThumbs, setShowThumbs] = useState(true);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext]);

  const thumbStripRef = useRef(null);

  useEffect(() => {
    const el = thumbStripRef.current;
    if (!el) return;
    const activeThumb = el.children[activeIndex];
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeIndex]);

  const modal = (
    <>
    <div
      className="fixed inset-0 z-[999] bg-[#0e0e0c] flex flex-col animate-[fadeIn_0.25s_ease]"
      onClick={onClose}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-5 sm:px-8 py-5 flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-white/50 text-xs font-bold tracking-[3px] uppercase">
          Galeri Foto
        </span>
        <button
          onClick={onClose}
          aria-label="Tutup galeri"
          className="text-white/60 hover:text-[#c9862a] transition-colors duration-200 p-2 -m-2 rounded-full hover:bg-white/5"
        >
          <HiX size={22} />
        </button>
      </div>

      {/* Main image area */}
      <div
        className="flex-1 flex items-center justify-center relative px-4 sm:px-24 min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onPrev}
          aria-label="Foto sebelumnya"
          className="absolute left-2 sm:left-6 z-10 w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 text-white/70 hover:text-[#c9862a] border border-white/10 transition-all duration-200"
        >
          <HiArrowLeft size={18} />
        </button>

        <img
          key={activeIndex}
          src={album[activeIndex]}
          alt={`Dokumentasi ${activeIndex + 1}`}
          className="max-h-[70vh] sm:max-h-[76vh] max-w-full object-contain select-none rounded-sm shadow-2xl animate-[fadeIn_0.3s_ease]"
          draggable={false}
        />

        <button
          onClick={onNext}
          aria-label="Foto selanjutnya"
          className="absolute right-2 sm:right-6 z-10 w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 text-white/70 hover:text-[#c9862a] border border-white/10 transition-all duration-200"
        >
          <HiArrowRight size={18} />
        </button>
      </div>

      {/* Bottom toolbar */}
      <div
        className="flex-shrink-0 flex flex-col items-center gap-4 px-5 sm:px-8 pb-6 pt-3"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Pill toolbar: counter + actions */}
        <div className="flex items-center gap-4 bg-white/[0.06] border border-white/10 rounded-full px-5 py-2.5">
          <span className="text-white/70 text-xs font-semibold tracking-wide tabular-nums">
            {activeIndex + 1} / {album.length}
          </span>
          <span className="w-px h-4 bg-white/15" />
          <button
            onClick={() => setShowThumbs((s) => !s)}
            aria-label="Tampilkan grid thumbnail"
            className={`transition-colors duration-200 ${
              showThumbs ? "text-[#c9862a]" : "text-white/60 hover:text-white"
            }`}
          >
            <HiOutlineViewGrid size={17} />
          </button>
        </div>

        {/* Thumbnail strip */}
        {showThumbs && (
          <div
            ref={thumbStripRef}
            className="flex gap-2 overflow-x-auto max-w-full justify-start sm:justify-center mx-auto pb-1"
            style={{ scrollbarWidth: "none" }}
          >
            {album.map((src, i) => (
              <button
                key={i}
                onClick={() => onSelect(i)}
                className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 overflow-hidden rounded-sm border-2 transition-all duration-200"
                style={{
                  borderColor: i === activeIndex ? "#c9862a" : "transparent",
                  opacity: i === activeIndex ? 1 : 0.4,
                }}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
    </>
  );

  return ReactDOM.createPortal(modal, document.body);
};

// ─── AlbumGallery: trigger card + state management ─────────────────────────────
const AlbumGallery = ({ album }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openAt = (index) => {
    setActiveIndex(index);
    setIsOpen(true);
  };
  const close = useCallback(() => setIsOpen(false), []);
  const prev = useCallback(
    () => setActiveIndex((i) => (i - 1 + album.length) % album.length),
    [album.length]
  );
  const next = useCallback(
    () => setActiveIndex((i) => (i + 1) % album.length),
    [album.length]
  );

  if (!album || album.length === 0) return null;

  return (
    <>
      <div className="mt-14 pt-10 border-t border-[#e8e0d0]">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-px w-10 bg-[#c9862a]" />
          <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
            Galeri Foto
          </span>
        </div>

        <button
          type="button"
          onClick={() => openAt(0)}
          className="group w-full sm:w-auto flex items-center gap-5 bg-[#f7f3ec] hover:bg-white border border-[#e8e0d0] hover:border-[#c9862a] px-6 py-5 transition-all duration-300"
        >
          {/* Stacked photo preview */}
          <div className="relative w-16 h-16 flex-shrink-0">
            <div className="absolute inset-0 bg-white border border-[#e8e0d0] rotate-6 group-hover:rotate-[10deg] transition-transform duration-300" />
            <div className="absolute inset-0.5 bg-white border border-[#e8e0d0] -rotate-3 group-hover:-rotate-6 transition-transform duration-300 overflow-hidden">
              <img src={album[0]} alt="" className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="text-left flex-1">
            <p className="font-serif text-lg font-bold text-[#1a1a18] flex items-center gap-2">
              <HiPhotograph className="text-[#c9862a] text-base" />
              Lihat Galeri Foto
            </p>
            <p className="text-xs text-[#a09880] mt-1">
              {album.length} foto dokumentasi kegiatan
            </p>
          </div>

          <HiArrowRight className="text-[#c9862a] flex-shrink-0 group-hover:translate-x-1 transition-transform duration-300" />
        </button>
      </div>

      {isOpen && (
        <AlbumLightbox
          album={album}
          activeIndex={activeIndex}
          onClose={close}
          onPrev={prev}
          onNext={next}
          onSelect={setActiveIndex}
        />
      )}
    </>
  );
};

// ─── Stories: Quill content renderer ──────────────────────────────────────────
const Stories = ({ story }) => {
  const [showContent, setShowContent] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-hide toast setelah beberapa detik
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 3000);
    return () => clearTimeout(t);
  }, [copied]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback untuk browser lama / non-HTTPS
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  if (!story) return null;

  // Parse tags dari string "kkn, desa" → array
  const tags = story.tags ? story.tags.split(",").map((t) => t.trim()) : [];

  const cleanContent = story.deskripsi
    ? story.deskripsi.replace(/&nbsp;/g, " ")
    : "";

  if (!showContent) {
    return (
      <section className="bg-white py-14 px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#a09880] text-sm animate-pulse">
            Memuat artikel...
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white py-14 px-4 relative">
      <div className="max-w-4xl mx-auto">
        <div className="flex gap-12 relative">
          {/* Left amber line */}
          <aside className="hidden lg:flex flex-col w-10 flex-shrink-0 pt-1">
            <div
              className="w-[2px] bg-gradient-to-b from-[#c9862a] to-transparent mx-auto"
              style={{ height: "100%", minHeight: 320 }}
            />
          </aside>

          {/* Quill-rendered content */}
          <div
            className="flex-1 min-w-0 ql-content"
            dangerouslySetInnerHTML={{ __html: cleanContent }}
          />
        </div>

        {/* Album Foto (galeri) */}
        <div>
          <AlbumGallery album={story.urlAlbum} />
        </div>

        {/* Tags + share */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-14 pt-10 border-t border-[#e8e0d0]">
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="bg-[#f7f3ec] border border-[#e8e0d0] text-[#6b6456] text-xs font-medium px-3 py-1.5 hover:border-[#c9862a] hover:text-[#c9862a] cursor-pointer transition-colors duration-200"
              >
                {tag}
              </span>
            ))}
          </div>
          <button
            onClick={handleShare}
            className="flex items-center gap-2 text-[#c9862a] text-sm font-bold tracking-wide border-b border-[#c9862a]/30 pb-0.5 hover:border-[#c9862a] transition-colors whitespace-nowrap group"
          >
            Bagikan Artikel
            <HiArrowRight
              size={13}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </button>
        </div>

        {/* Back navigation */}
        <div className="mt-10">
          <a
            href="/stories"
            className="flex items-center gap-3 text-[#3a3a34] hover:text-[#c9862a] transition-colors duration-200 group w-fit"
          >
            <div className="w-10 h-10 border border-[#e8e0d0] group-hover:border-[#c9862a] flex items-center justify-center transition-colors duration-200">
              <HiArrowLeft size={16} />
            </div>
            <span className="text-sm font-bold tracking-wide uppercase">
              Kembali ke Story
            </span>
          </a>
        </div>
      </div>

      {/* ── Toast Notification ── */}
      <div
        className="fixed bottom-5 inset-x-0 z-50 pointer-events-none px-4 flex justify-center"
        style={{
          transform: copied ? "translateY(0)" : "translateY(16px)",
          opacity: copied ? 1 : 0,
          transition: "all 0.35s cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        <div className="flex items-center gap-3 bg-[#1a1a18] text-white pl-3 pr-4 py-3 rounded-2xl shadow-xl shadow-black/30 border border-white/10 pointer-events-auto max-w-xs w-full sm:w-auto">
          <div className="w-8 h-8 rounded-full bg-[#c9862a] flex items-center justify-center flex-shrink-0">
            <HiCheck size={15} className="text-white" />
          </div>
          <span className="text-[13px] font-semibold leading-tight">Tautan berhasil disalin!</span>
        </div>
      </div>

      {/* Quill content styles */}
      <style>{`
        .ql-content {
          color: #3a3a34;
          font-size: 15px;
          line-height: 1.9;
          text-align: justify;
          overflow-wrap: break-word;
          word-break: normal;
          white-space: normal;
        }
        .ql-content p {
          margin-bottom: 1.25rem;
          color: #4a4a44;
        }
        .ql-content h1 {
          font-family: Georgia, serif;
          font-size: 2rem;
          font-weight: 700;
          color: #1a1a18;
          margin: 2.5rem 0 1rem;
          line-height: 1.2;
        }
        .ql-content h2 {
          font-family: Georgia, serif;
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a1a18;
          margin: 2.5rem 0 1rem;
          line-height: 1.3;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #e8e0d0;
        }
        .ql-content h2::before {
          content: '';
          display: inline-block;
          width: 3px;
          height: 1em;
          background: #c9862a;
          margin-right: 10px;
          vertical-align: middle;
          border-radius: 1px;
        }
        .ql-content h3 {
          font-family: Georgia, serif;
          font-size: 1.2rem;
          font-weight: 700;
          color: #1a1a18;
          margin: 2rem 0 0.75rem;
        }
        .ql-content strong {
          font-weight: 700;
          color: #1a1a18;
        }
        .ql-content em {
          font-style: italic;
          color: #5a5a50;
        }
        .ql-content ul, .ql-content ol {
          margin: 1rem 0 1.5rem 0;
          padding-left: 0;
          list-style: none;
        }
        .ql-content ul li {
          position: relative;
          padding: 0.6rem 0.75rem 0.6rem 2.5rem;
          margin-bottom: 0.4rem;
          background: #f7f3ec;
          border-left: 3px solid #c9862a;
          color: #3a3a34;
          font-size: 0.9rem;
          line-height: 1.7;
        }
        .ql-content ul li::before {
          content: '✳';
          position: absolute;
          left: 0.65rem;
          top: 0.6rem;
          color: #c9862a;
          font-size: 0.75rem;
          font-weight: 700;
        }
        .ql-content ol {
          counter-reset: ol-counter;
        }
        .ql-content ol li {
          position: relative;
          padding: 0.5rem 0.75rem 0.5rem 2.5rem;
          margin-bottom: 0.4rem;
          color: #3a3a34;
          font-size: 0.9rem;
          line-height: 1.7;
          counter-increment: ol-counter;
        }
        .ql-content ol li::before {
          content: counter(ol-counter) ".";
          position: absolute;
          left: 0.5rem;
          top: 0.5rem;
          color: #c9862a;
          font-weight: 700;
          font-size: 0.85rem;
        }
        .ql-content blockquote {
          border-left: 4px solid #c9862a;
          background: #f7f3ec;
          padding: 1.25rem 1.5rem;
          margin: 2rem 0;
          font-family: Georgia, serif;
          font-size: 1.15rem;
          font-style: italic;
          color: #1a1a18;
          line-height: 1.65;
        }
        .ql-content a {
          color: #c9862a;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .ql-content a:hover {
          color: #e09b3a;
        }
        .ql-content img {
          max-width: 100%;
          height: auto;
          margin: 1.5rem 0;
        }
        .ql-content hr {
          border: none;
          border-top: 1px solid #e8e0d0;
          margin: 2rem 0;
        }
        .ql-content pre {
          background: #1a1a18;
          color: #f0ece3;
          padding: 1.25rem 1.5rem;
          border-radius: 2px;
          font-size: 0.875rem;
          overflow-x: auto;
          margin: 1.5rem 0;
        }
      `}</style>
    </section>
  );
};

// ─── Comments Section ──────────────────────────────────────────────────────────
const Comments = () => {
  const [nama, setNama] = useState("");
  const [komentar, setKomentar] = useState("");
  const [dataKomentar, setDataKomentar] = useState([]);
  const { id } = useParams();
  const ref = useRef(null);
  const inView = useInView(ref);

  useEffect(() => {
    getComments();
  }, [id]);

  const getComments = async () => {
    try {
      const apiUrl = process.env.REACT_APP_URL_API;
      const response = await axios.get(
        `${apiUrl}/komentar?storiesId=${id}`
      );
      setDataKomentar(response.data);
    } catch (error) {
      console.error(
        "Error fetching comments:",
        error.response ? error.response.data : error.message
      );
    }
  };

  const saveComment = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("nama", nama);
    formData.append("komentar", komentar);
    formData.append("storiesId", id);

    const jsonData = {};
    formData.forEach((value, key) => {
      jsonData[key] = value;
    });
    
    const apiUrl = process.env.REACT_APP_URL_API;

    try {
      await axios.post(
        `${apiUrl}/komentar`,
        jsonData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      getComments();
    } catch (error) {
      console.error(
        "Error:",
        error.response ? error.response.data : error.message
      );
    }
  };

  return (
    <section
      ref={ref}
      className="bg-[#f7f3ec] py-16 px-4 border-t border-[#e8e0d0]"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div
          className="mb-10"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.7s ease",
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-10 bg-[#c9862a]" />
            <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
              Diskusi
            </span>
          </div>
          <h2 className="font-serif text-3xl font-bold text-[#1a1a18]">
            Komentar ({dataKomentar.length})
          </h2>
        </div>

        {/* Comment form */}
        <form onSubmit={saveComment} className="mb-12">
        <div
          className="bg-white border border-[#e8e0d0] p-7 md:p-9 mb-10"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.7s ease 0.15s",
          }}
        >
          <h3 className="font-serif text-lg font-bold text-[#1a1a18] mb-6">
            Tinggalkan Komentar
          </h3>

          <div className="space-y-5">
            {/* Name field */}
            <div>
              <label className="block text-xs font-bold text-[#3a3a34] tracking-widest uppercase mb-2">
                Nama <span className="text-[#c9862a]">*</span>
              </label>
              <input
                type="text"
                placeholder="Nama Lengkap Anda"
                value={nama}
                onChange={(e) => setNama(e.target.value)}
                className="w-full bg-[#faf6ef] border border-[#e8e0d0] text-[#1a1a18] text-sm px-4 py-3 outline-none focus:border-[#c9862a] transition-colors duration-200 placeholder-[#b0a898]"
              />
            </div>

            {/* Comment field */}
            <div>
              <label className="block text-xs font-bold text-[#3a3a34] tracking-widest uppercase mb-2">
                Komentar <span className="text-[#c9862a]">*</span>
              </label>
              <textarea
                rows={5}
                placeholder="Tulis komentar Anda di sini..."
                value={komentar}
                onChange={(e) => setKomentar(e.target.value)}
                className="w-full bg-[#faf6ef] border border-[#e8e0d0] text-[#1a1a18] text-sm px-4 py-3 outline-none focus:border-[#c9862a] transition-colors duration-200 placeholder-[#b0a898] resize-none"
              />
            </div>

            {/* Submit button */}
            <div className="flex justify-end">
              <button type="submit" className="flex items-center gap-3 bg-[#c9862a] hover:bg-[#e09b3a] text-[#1a1a18] font-bold text-sm tracking-widest uppercase px-8 py-3 transition-colors duration-200 group">
                Kirim Komentar
                <HiArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-10">
          <div className="flex-1 h-px bg-[#e8e0d0]" />
          <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
          <div className="flex-1 h-px bg-[#e8e0d0]" />
        </div>

        {/* Comment list */}
        <div className="space-y-5">
          {dataKomentar.map(({ nama, createdAt, komentar }, i) => (
            <div
              key={i}
              className="bg-white border border-[#e8e0d0] p-6 md:p-7 hover:border-[#c9862a]/30 transition-colors duration-200"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(16px)",
                transition: `all 0.6s ease ${0.3 + i * 0.12}s`,
              }}
            >
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="w-11 h-11 bg-[#f7f3ec] border border-[#e8e0d0] flex items-center justify-center flex-shrink-0">
                  <FaUserCircle className="text-[#c9862a] text-2xl" />
                </div>

                <div className="flex-1 min-w-0">
                  {/* Name + date */}
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="font-bold text-sm text-[#1a1a18]">
                      {nama}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-[#c9862a]" />
                    <span className="text-xs text-[#a09880]">{formatDate(createdAt)}</span>
                  </div>

                  {/* Comment text */}
                  <p className="text-[#6b6456] text-sm leading-relaxed">
                    {komentar}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const ViewStories = () => {
  const { slug } = useParams(); // ① ambil slug dari URL
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getStoryBySlug = async () => {
      try {
        const apiUrl = process.env.REACT_APP_URL_API;
        const response = await axios.get(`${apiUrl}/view-stories/slug/${slug}`);
        setStory(response.data); // ② simpan ke state
      } catch (err) {
        console.error("Error fetching story data:", err);
        setError("Gagal memuat artikel.");
      } finally {
        setLoading(false);
      }
    };

    if (slug) getStoryBySlug();
  }, [slug]); // ③ re-fetch jika slug berubah

  if (loading)
    return (
      <main className="flex items-center justify-center min-h-screen font-homepage">
        <span className="text-[#a09880] text-sm animate-pulse">
          Memuat artikel...
        </span>
      </main>
    );

  if (error)
    return (
      <main className="flex items-center justify-center min-h-screen font-homepage">
        <span className="text-red-500 text-sm">{error}</span>
      </main>
    );

  return (
    <main className="overflow-x-hidden font-homepage bg-white">
      <Navbar />
      <section id="hero">
        <Hero story={story} />
      </section>{" "}
      {/* ④ pass story */}
      <section id="stories">
        <Stories story={story} />
      </section>
      <section id="comments">
        <Comments />
      </section>
      <section id="footer">
        <Footer />
      </section>
    </main>
  );
};

export default ViewStories;